/**
 * Auswertungen: Auftragsbestand und Umsatzentwicklung.
 *
 * Die Seite beantwortet vier Fragen, die im Tagesgeschäft zählen:
 * Was ist verkauft, aber noch nicht geliefert? Wie läuft der Monat? Wie
 * steht das Jahr gegen das Vorjahr? Und was ist an Geld noch offen?
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { formatAmount, formatDate, formatQuantity } from '@erp/shared';

import { api, withQuery } from '../api/client';
import { PageHeader } from '../components/Layout';
import { changePercent } from '../components/chart-math';
import { BarChart, GroupedBarChart, StatTile } from '../components/charts';
import { EmptyState, LoadingBlock } from '../components/ui';

type Dashboard = {
  today: string;
  currentYear: number;
  revenue: {
    today: number;
    month: number;
    lastMonth: number;
    year: number;
    previousYear: number;
  };
  backlog: {
    total: number;
    orderCount: number;
    orders: {
      id: string;
      number: string | null;
      documentDate: string;
      status: string;
      netTotal: number;
      partnerName: string;
      openAmount: number;
    }[];
  };
  openItems: { total: number; overdue: number; count: number };
  draftCount: number;
  lowStockCount: number;
  daily: { date: string; net: number; count: number }[];
  monthly: { month: number; current: number; previous: number }[];
  topItems: {
    itemId: string | null;
    itemNumber: string | null;
    description: string;
    quantity: number;
    net: number;
  }[];
};

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mär',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dez',
];

const MONTH_NAMES = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

const STATUS_LABELS: Record<string, string> = {
  open: 'Offen',
  partially_delivered: 'Teilweise geliefert',
  delivered: 'Geliefert',
  completed: 'Abgeschlossen',
};

/** Veränderung in Prozent; null, wenn kein sinnvoller Vergleich möglich ist. */
function change(current: number, previous: number): { percent: number; label: string } | null {
  const percent = changePercent(current, previous);
  return percent === null ? null : { percent, label: '' };
}

export function AnalyticsPage() {
  const [days, setDays] = useState(30);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dashboard', days],
    queryFn: () => api.get<Dashboard>(withQuery('/api/dashboard', { days })),
    // Kennzahlen dürfen ruhig ein paar Sekunden alt sein.
    staleTime: 30_000,
  });

  if (isLoading) return <LoadingBlock text="Kennzahlen werden berechnet …" />;
  if (isError) return <div className="alert alert-error">{(error as Error).message}</div>;
  if (!data) return null;

  const monthIndex = Number(data.today.slice(5, 7)) - 1;
  const monthName = MONTH_NAMES[monthIndex] ?? '';

  // Vorjahresvergleich nur bis zum heutigen Monat - der Rest des Vorjahres
  // gehört nicht in einen Vergleich des laufenden Jahres.
  const yearToDatePrevious = data.monthly
    .slice(0, monthIndex + 1)
    .reduce((sum, entry) => sum + entry.previous, 0);

  const dailyData = data.daily.map((entry) => ({
    label: entry.date.slice(8, 10),
    title: formatDate(entry.date),
    value: entry.net,
    note:
      entry.count === 0
        ? 'kein Beleg'
        : `${entry.count} ${entry.count === 1 ? 'Beleg' : 'Belege'}`,
    emphasized: entry.date === data.today,
  }));

  const monthlyData = data.monthly.map((entry) => ({
    label: MONTH_LABELS[entry.month - 1]!,
    title: `${MONTH_NAMES[entry.month - 1]}`,
    current: entry.current,
    previous: entry.previous,
  }));

  const hasAnyRevenue = data.revenue.year !== 0 || data.revenue.previousYear !== 0;

  return (
    <>
      <PageHeader
        title="Auswertungen"
        subtitle={`Stand ${formatDate(data.today)}`}
      />

      {/* --- Kennzahlen ---------------------------------------------------- */}
      <div className="tile-grid">
        <StatTile
          label="Auftragsbestand"
          value={`${formatAmount(data.backlog.total)} €`}
          note={
            data.backlog.orderCount === 0
              ? 'keine offenen Aufträge'
              : `${data.backlog.orderCount} ${data.backlog.orderCount === 1 ? 'Auftrag' : 'Aufträge'} noch nicht geliefert`
          }
          hero
        />
        <StatTile label="Umsatz heute" value={`${formatAmount(data.revenue.today)} €`} note="netto" />
        <StatTile
          label={`Umsatz ${monthName}`}
          value={`${formatAmount(data.revenue.month)} €`}
          delta={change(data.revenue.month, data.revenue.lastMonth)}
          note={`Vormonat ${formatAmount(data.revenue.lastMonth)} €`}
        />
        <StatTile
          label={`Umsatz ${data.currentYear}`}
          value={`${formatAmount(data.revenue.year)} €`}
          delta={change(data.revenue.year, yearToDatePrevious)}
          note={`Vorjahr bis heute ${formatAmount(yearToDatePrevious)} €`}
        />
        <StatTile
          label="Offene Forderungen"
          value={`${formatAmount(data.openItems.total)} €`}
          note={`${data.openItems.count} ${data.openItems.count === 1 ? 'Rechnung' : 'Rechnungen'}`}
        />
        {data.openItems.overdue > 0 ? (
          <StatTile
            label="Davon überfällig"
            value={`${formatAmount(data.openItems.overdue)} €`}
            tone="danger"
            note="Zahlungsziel überschritten"
          />
        ) : null}
      </div>

      {/* --- Hinweise ------------------------------------------------------ */}
      {data.draftCount > 0 || data.lowStockCount > 0 ? (
        <div className="row" style={{ gap: 16, marginTop: 14, flexWrap: 'wrap', fontSize: 13.5 }}>
          {data.draftCount > 0 ? (
            <span className="muted">
              {data.draftCount} {data.draftCount === 1 ? 'Beleg liegt' : 'Belege liegen'} als
              Entwurf und {data.draftCount === 1 ? 'ist' : 'sind'} noch nicht festgeschrieben.
            </span>
          ) : null}
          {data.lowStockCount > 0 ? (
            <span className="muted">
              {data.lowStockCount} {data.lowStockCount === 1 ? 'Artikel liegt' : 'Artikel liegen'}{' '}
              unter dem Meldebestand. <Link to="/einkauf/bestellvorschlag">Bestellvorschlag</Link>
            </span>
          ) : null}
        </div>
      ) : null}

      {/* --- Tagesumsatz --------------------------------------------------- */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header">
          <div>
            <h2>Umsatz je Tag</h2>
            <div className="faint" style={{ fontSize: 12.5 }}>
              Nettoumsatz aus Rechnungen und Gutschriften, letzte {days} Tage
            </div>
          </div>
          <div className="row" style={{ gap: 6 }}>
            {[14, 30, 90].map((option) => (
              <button
                key={option}
                type="button"
                className={`btn btn-sm${days === option ? ' btn-primary' : ''}`}
                onClick={() => setDays(option)}
              >
                {option} Tage
              </button>
            ))}
          </div>
        </div>
        <div className="card-body" style={{ paddingTop: 8 }}>
          <BarChart
            data={dailyData}
            labelEvery={days > 60 ? 7 : days > 20 ? 3 : 1}
            emptyText="Im Zeitraum wurde noch nichts abgerechnet."
          />
          <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
            Der heutige Tag ist hervorgehoben. Zahlen erscheinen beim Überfahren eines Tages.
          </div>
        </div>
      </div>

      {/* --- Monatsumsatz -------------------------------------------------- */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2>Umsatz je Monat</h2>
            <div className="faint" style={{ fontSize: 12.5 }}>
              {data.currentYear} im Vergleich zu {data.currentYear - 1}
            </div>
          </div>
        </div>
        <div className="card-body" style={{ paddingTop: 8 }}>
          {hasAnyRevenue ? (
            <GroupedBarChart
              data={monthlyData}
              currentLabel={String(data.currentYear)}
              previousLabel={String(data.currentYear - 1)}
            />
          ) : (
            <div className="chart-empty">Noch keine abgerechneten Umsätze.</div>
          )}
        </div>
      </div>

      {/* --- Auftragsbestand ----------------------------------------------- */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2>Offene Aufträge</h2>
            <div className="faint" style={{ fontSize: 12.5 }}>
              Bewertet mit dem noch nicht gelieferten Anteil, nicht mit dem Auftragswert
            </div>
          </div>
          <Link to="/verkauf/auftraege" className="btn btn-sm">
            Alle Aufträge
          </Link>
        </div>

        {data.backlog.orders.length === 0 ? (
          <EmptyState
            title="Kein offener Auftragsbestand"
            hint="Alle festgeschriebenen Aufträge sind vollständig geliefert."
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th className="shrink">Auftrag</th>
                  <th className="shrink">Datum</th>
                  <th>Kunde</th>
                  <th className="shrink">Status</th>
                  <th className="numeric">Auftragswert</th>
                  <th className="numeric">Davon offen</th>
                </tr>
              </thead>
              <tbody>
                {data.backlog.orders.map((order) => (
                  <tr key={order.id}>
                    <td className="mono">
                      <Link to={`/verkauf/auftraege/${order.id}`}>{order.number}</Link>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(order.documentDate)}</td>
                    <td>{order.partnerName}</td>
                    <td className="faint">{STATUS_LABELS[order.status] ?? order.status}</td>
                    <td className="numeric muted">{formatAmount(order.netTotal)}</td>
                    <td className="numeric" style={{ fontWeight: 600 }}>
                      {formatAmount(order.openAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* --- Bestseller ---------------------------------------------------- */}
      {data.topItems.length > 0 ? (
        <div className="card">
          <div className="card-header">
            <h2>Umsatzstärkste Artikel {data.currentYear}</h2>
          </div>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th className="shrink">Nummer</th>
                  <th>Bezeichnung</th>
                  <th className="numeric">Menge</th>
                  <th className="numeric">Umsatz netto</th>
                </tr>
              </thead>
              <tbody>
                {data.topItems.map((item, index) => (
                  <tr key={item.itemId ?? index}>
                    <td className="mono">
                      {item.itemId ? (
                        <Link to={`/artikel/${item.itemId}`}>{item.itemNumber}</Link>
                      ) : (
                        item.itemNumber
                      )}
                    </td>
                    <td>{item.description}</td>
                    <td className="numeric muted">{formatQuantity(item.quantity)}</td>
                    <td className="numeric" style={{ fontWeight: 500 }}>
                      {formatAmount(item.net)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <p className="faint" style={{ fontSize: 12, marginTop: 12 }}>
        Umsatz heißt hier: Nettobetrag festgeschriebener Rechnungen und Gutschriften nach
        Belegdatum. Stornierte Rechnungen und ihre Stornobelege heben sich gegenseitig auf.
      </p>
    </>
  );
}
